import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { buildApiUrl } from '../config';

export const useTradeStatusHub = ({
  enabled = true,
  hubUrl = buildApiUrl('/tradeStatusHub'),
} = {}) => {
  const [connectionState, setConnectionState] = useState('Disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [isRunning, setIsRunning] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [tradeUpdate, setTradeUpdate] = useState(null);
  const connectionRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
      setConnectionState('Disconnected');
      setIsConnected(false);
      return;
    }

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { withCredentials: true })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    connectionRef.current = newConnection;

    newConnection.onreconnecting(() => {
      setConnectionState('Reconnecting');
      setIsConnected(false);
    });

    newConnection.onreconnected(() => {
      setConnectionState('Connected');
      setIsConnected(true);
    });

    newConnection.onclose(() => {
      setConnectionState('Disconnected');
      setIsConnected(false);
    });

    newConnection.on('TradeWorkerStatusUpdate', (running) => {
      setIsRunning(Boolean(running));
      setLastUpdatedAt(Date.now());
    });

    newConnection.on('TradeWorkerUpdate', (payload) => {
      let parsed = null;
      if (typeof payload === 'string') {
        try {
          parsed = JSON.parse(payload);
        } catch (_) {
          parsed = null;
        }
      } else if (payload && typeof payload === 'object') {
        parsed = payload;
      }
      setTradeUpdate({
        raw: payload,
        data: parsed,
        receivedAt: Date.now(),
      });
    });

    const startConnection = async () => {
      try {
        setConnectionState('Connecting');
        await newConnection.start();
        setConnectionState('Connected');
        setIsConnected(true);
      } catch (_) {
        setConnectionState('Disconnected');
        setIsConnected(false);
        setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [enabled, hubUrl]);

  const requestStatus = useCallback(async () => {
    if (!connectionRef.current || !isConnected) return;
    try {
      await connectionRef.current.invoke('GetWorkerStatus');
    } catch (_) {
      // no-op
    }
  }, [isConnected]);

  return {
    connectionState,
    isConnected,
    isRunning,
    lastUpdatedAt,
    tradeUpdate,
    requestStatus,
  };
};
